import { api } from '@/lib/api';
import { CURRENCY_BASE, IAM_BASE } from '@/api/paths';
import { ApiShapeError, expectItems, unwrap } from '@/lib/task-api';
import { MEMBER_TYPES, ROLE_CODES, USER_TYPES, type MemberType, type RoleCode, type UserType } from '@/auth/types';

/**
 * PLATFORM YONETIMI UCLARI (`/admin/v1/...`) — tipli sarmalayicilar +
 * HAM -> GORUNUM donusumu.
 *
 * Neden `src/api/iam.ts` degil: o dosya iskeletin ORTAK yuzeyi ve baska
 * feature'lar da ona yaziyor. Bu uclar yalniz bu feature tarafindan
 * kullaniliyor (bkz. src/features/organization/api.ts — ayni gerekce).
 *
 * ================================ SOZLESME =================================
 * Her uc `{ code: 'success' | 'error', message, ... }` doner ve HTTP 200 +
 * `code: 'error'` MUMKUN. `unwrap` (src/lib/task-api.ts) bu yuzden HER
 * yanittan geciyor; firlattigi ApiError'in MESAJI sunucunun KODUDUR
 * ('CANNOT_MODIFY_SELF' gibi) — cevirisi error-message.ts'te yapilir.
 *
 * Liste uclari `{ items, page }` kullanir; `page.total` YOKTUR, sayfalama
 * `hasMore` uzerinden yurur ve "3 / 120" gibi bir sayac cizilemez.
 *
 * ============================ SEKIL SESSIZ DUSMEZ ==========================
 * `?? []` yazmak cazip ama tehlikeli: alan adi degisirse ekran hatasiz calisir
 * ve "kullanici yok" der. Ne tsc ne test bunu yakalar. Bu yuzden beklenmeyen
 * sekil `ApiShapeError` FIRLATIR — "kayit yok" ile "yaniti anlayamadim" ayni
 * ekrani gostermemeli.
 *
 * =========================== YETKI VE GIZLILIK =============================
 * Butun uclar ag gecidinde `roles: ['super_admin']` ile korunuyor ve serviste
 * BIR KEZ DAHA kontrol ediliyor. Buradaki tipler sunucunun PROJEKSIYONUNU
 * yansitir: `user_password` hicbir zaman secilmiyor ve TC kimlik BILINCLI
 * olarak projeksiyonda yok — bu alanlar icin tip de yazilmadi ki ileride
 * "sunucu gonderiyorsa gosterelim" refleksi olusmasin.
 *
 * KIMLIKLER STRING. pg int8'i string dondurur; `Number()`a cevirip `===`
 * karsilastirmak 2^53 ustunde sessizce yanlis sonuc verir. Sunucunun kendini
 * degistirme korumasi (CANNOT_MODIFY_SELF) ayni disipline dayaniyor.
 * =========================================================================== */

type Envelope = { code?: string; message?: string };

/* -------------------------------------------------------------------------- */
/* Ham deger cevirileri                                                        */
/* -------------------------------------------------------------------------- */

const asId = (value: unknown): string => {
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'bigint') return String(value);
  return '';
};

/** Kimlik alani NULL olabiliyorsa (organizationId) bos dize degil null gerekir. */
const asNullableId = (value: unknown): string | null => {
  if (value === null || value === undefined) return null;
  const id = asId(value);
  return id === '' ? null : id;
};

const asText = (value: unknown): string => (typeof value === 'string' ? value : '');

const asNullableText = (value: unknown): string | null =>
  typeof value === 'string' && value.length > 0 ? value : null;

/**
 * SAYAC alanlari (memberCount, accountCount, permissionCount) `count(*)`
 * sonucu, yani yine bigint ve yine STRING gelebilir. Number() burada BILINCLI
 * ve guvenli: bir sayac kimlik degil ve 2^53'e yaklasmasi mumkun degil.
 */
const asCount = (value: unknown): number => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

/** Parasal/olcum degerleri de string gelebiliyor (numeric sutunlari). */
const asNumber = (value: unknown): number => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

/** Sunucu `is_active` icin gercek boolean doner; yine de savunmali okunur. */
const asBool = (value: unknown): boolean => value === true;

const asUserType = (value: unknown): UserType | null =>
  USER_TYPES.includes(value as UserType) ? (value as UserType) : null;

const asRoleCode = (value: unknown): RoleCode =>
  ROLE_CODES.includes(value as RoleCode) ? (value as RoleCode) : 'user';

const asMemberType = (value: unknown): MemberType | null =>
  MEMBER_TYPES.includes(value as MemberType) ? (value as MemberType) : null;

/* -------------------------------------------------------------------------- */
/* Sayfa ustverisi                                                             */
/* -------------------------------------------------------------------------- */

/**
 * LISTE SOZLESMESI: `page = { limit, offset, returned, hasMore, sort,
 * snapshotAt }`. `total` YOK.
 */
export type PageMeta = {
  limit: number;
  offset: number;
  returned: number;
  hasMore: boolean;
  /**
   * Ilk sayfanin cektigi an. Sonraki sayfalarda GERI GONDERILIR: aksi halde
   * araya yazilan yeni bir kayit pencereyi kaydirir ve ayni satir iki sayfada
   * birden gorunur.
   */
  snapshotAt: string | null;
};

export type Paged<T> = { items: T[]; page: PageMeta };

export const ADMIN_PAGE_SIZE = 25;

const expectPage = (raw: unknown, endpoint: string, fallbackReturned: number): PageMeta => {
  if (typeof raw !== 'object' || raw === null) {
    throw new ApiShapeError(`${endpoint}.page nesne degil`);
  }
  const page = raw as Record<string, unknown>;
  return {
    limit: asCount(page.limit) || ADMIN_PAGE_SIZE,
    offset: asCount(page.offset),
    returned: page.returned === undefined ? fallbackReturned : asCount(page.returned),
    hasMore: page.hasMore === true,
    snapshotAt: typeof page.snapshotAt === 'string' ? page.snapshotAt : null,
  };
};

/** Sayfalanmis GET — zarf + `items` + `page` seklini TEK yerde dogrular. */
const pagedGet = async <T>(
  base: string,
  path: string,
  params: Record<string, string | number | undefined>,
  toRow: (raw: Record<string, unknown>) => T,
): Promise<Paged<T>> => {
  const { data } = await api.get<Envelope & { items?: unknown; page?: unknown }>(
    `${base}${path}`,
    { params },
  );
  unwrap(data);
  const items = expectItems(data, path).map(toRow);
  return { items, page: expectPage(data.page, path, items.length) };
};

/** Sayfalama parametreleri — `snapshotAt` yalniz VARSA gonderilir. */
const pageParams = (input: {
  offset: number;
  snapshotAt: string | null;
  sort?: string | undefined;
}): Record<string, string | number | undefined> => ({
  limit: ADMIN_PAGE_SIZE,
  offset: input.offset,
  ...(input.snapshotAt === null ? {} : { snapshotAt: input.snapshotAt }),
  ...(input.sort === undefined ? {} : { sort: input.sort }),
});

/* -------------------------------------------------------------------------- */
/* Kullanicilar                                                                */
/* -------------------------------------------------------------------------- */

/**
 * Liste satiri. Alanlar sunucunun DONDURDUKLERININ ALT KUMESI: ekranda
 * gorunmeyen bir alani tipe yazmak, birinin onu goruntulemesini davet eder.
 */
export type PlatformUserRow = {
  id: string;
  userFullName: string;
  userEmail: string;
  userPhone: string;
  userType: UserType | null;
  isActive: boolean;
  roleCode: RoleCode;
  organizationId: string | null;
  organizationName: string | null;
  memberType: MemberType | null;
  createdAt: string;
};

const toUserRow = (raw: Record<string, unknown>): PlatformUserRow => ({
  id: asId(raw.id),
  userFullName: asText(raw.userFullName),
  userEmail: asText(raw.userEmail),
  userPhone: asText(raw.userPhone),
  userType: asUserType(raw.userType),
  isActive: asBool(raw.isActive),
  roleCode: asRoleCode(raw.userRoleCode),
  organizationId: asNullableId(raw.organizationId),
  organizationName: asNullableText(raw.organizationName),
  memberType: asMemberType(raw.memberType),
  createdAt: asText(raw.createdAt),
});

/** Sunucunun kabul ettigi siralama alanlari (db-scripts adminUserSortMap). */
export const USER_SORT_OPTIONS = [
  { value: 'createdAt:desc', labelKey: 'admin.users.sortNewest' },
  { value: 'userFullName:asc', labelKey: 'admin.users.sortName' },
  { value: 'userEmail:asc', labelKey: 'admin.users.sortEmail' },
] as const;

export type UserListFilters = {
  /**
   * Serbest arama. TARAYICI ADRESINE YAZILMAZ (bkz. PlatformUsersPage):
   * yoneticinin arattigi sey cogu zaman bir e-posta adresidir ve adres
   * cubugu; gecmis, yer imi ve referrer ile birlikte tasinir.
   */
  q: string;
  userType: UserType | null;
  isActive: boolean | null;
  organizationId: string | null;
  sort: string;
};

export const EMPTY_USER_FILTERS: UserListFilters = {
  q: '',
  userType: null,
  isActive: null,
  organizationId: null,
  sort: 'createdAt:desc',
};

export const fetchPlatformUsers = (input: {
  filters: UserListFilters;
  offset: number;
  snapshotAt: string | null;
}): Promise<Paged<PlatformUserRow>> => {
  const { filters } = input;
  const trimmed = filters.q.trim();
  return pagedGet(
    IAM_BASE,
    '/admin/v1/users',
    {
      ...pageParams({ offset: input.offset, snapshotAt: input.snapshotAt, sort: filters.sort }),
      ...(trimmed === '' ? {} : { q: trimmed }),
      ...(filters.userType === null ? {} : { userType: filters.userType }),
      ...(filters.isActive === null ? {} : { isActive: String(filters.isActive) }),
      ...(filters.organizationId === null ? {} : { organizationId: filters.organizationId }),
    },
    toUserRow,
  );
};

export type PlatformUserOrganization = {
  organizationId: string;
  organizationName: string;
  organizationTypeCode: string;
  organizationIsActive: boolean;
  memberType: MemberType | null;
  membershipIsActive: boolean;
  joinedAt: string | null;
};

export type PlatformUserConsent = {
  kvkkAccepted: boolean;
  kvkkAcceptedAt: string | null;
  usageAgreementAccepted: boolean;
  usageAgreementAcceptedAt: string | null;
  legalDocVersion: string | null;
  mustChangePassword: boolean;
};

export type PlatformUserDetail = {
  user: PlatformUserRow & { userCity: string; userDistrict: string };
  roleCode: RoleCode;
  organization: PlatformUserOrganization | null;
  consent: PlatformUserConsent;
  accountCount: number;
  activeAccountCount: number;
};

const toUserOrganization = (raw: unknown): PlatformUserOrganization | null => {
  // Musteri kullanicisinda sunucu ACIKCA `null` doner — uyeligi yok.
  if (typeof raw !== 'object' || raw === null) return null;
  const value = raw as Record<string, unknown>;
  const id = asNullableId(value.organizationId);
  if (id === null) return null;
  return {
    organizationId: id,
    organizationName: asText(value.organizationName),
    organizationTypeCode: asText(value.organizationTypeCode),
    organizationIsActive: asBool(value.organizationIsActive),
    memberType: asMemberType(value.memberType),
    membershipIsActive: asBool(value.membershipIsActive),
    joinedAt: asNullableText(value.joinedAt),
  };
};

const toConsent = (raw: unknown): PlatformUserConsent => {
  const value = (typeof raw === 'object' && raw !== null ? raw : {}) as Record<string, unknown>;
  return {
    kvkkAccepted: asBool(value.kvkkAccepted),
    kvkkAcceptedAt: asNullableText(value.kvkkAcceptedAt),
    usageAgreementAccepted: asBool(value.usageAgreementAccepted),
    usageAgreementAcceptedAt: asNullableText(value.usageAgreementAcceptedAt),
    legalDocVersion: asNullableText(value.legalDocVersion),
    mustChangePassword: asBool(value.mustChangePassword),
  };
};

/**
 * `userId` SORGU DIZESINDE gider — sunucunun sozlesmesi bu ve degistirilemez.
 * Onemli olan istemcinin bu adresi KAYDA GECIRMEMESI: uygulamanin kendi rotasi
 * yol parametresi kullanir (`/yonetim/kullanicilar/:userId`), istekler
 * gunlenmez ve arama metni adres cubuguna hic yazilmaz.
 */
export const fetchPlatformUser = async (userId: string): Promise<PlatformUserDetail> => {
  const { data } = await api.get<
    Envelope & {
      user?: unknown;
      role?: unknown;
      organization?: unknown;
      consent?: unknown;
      accountCount?: unknown;
      activeAccountCount?: unknown;
    }
  >(`${IAM_BASE}/admin/v1/users/detail`, { params: { userId } });
  unwrap(data);

  if (typeof data.user !== 'object' || data.user === null) {
    throw new ApiShapeError('/admin/v1/users/detail.user nesne degil');
  }
  const raw = data.user as Record<string, unknown>;
  const role = (typeof data.role === 'object' && data.role !== null ? data.role : {}) as Record<
    string,
    unknown
  >;

  return {
    user: {
      ...toUserRow(raw),
      userCity: asText(raw.userCity),
      userDistrict: asText(raw.userDistrict),
    },
    roleCode: asRoleCode(role.userRoleCode ?? raw.userRoleCode),
    organization: toUserOrganization(data.organization),
    consent: toConsent(data.consent),
    accountCount: asCount(data.accountCount),
    activeAccountCount: asCount(data.activeAccountCount),
  };
};

/**
 * Aktiflik degisimi. Sunucu ayni islemde o kullanicinin TUM oturumlarini iptal
 * eder. Kendini pasiflestirme (CANNOT_MODIFY_SELF) ve son etkin super_admin'i
 * dusurme (LAST_SUPER_ADMIN_PROTECTED) 400 ile REDDEDILIR — arayuz bunlari
 * yeniden uygulamaz, yalniz bariz olani (kendisi) butonu kapatarak gosterir.
 */
export const setUserStatus = async (input: {
  userId: string;
  isActive: boolean;
}): Promise<void> => {
  const { data } = await api.post<Envelope>(`${IAM_BASE}/admin/v1/users/status`, input);
  unwrap(data);
};

export const setUserRole = async (input: {
  userId: string;
  userRoleCode: RoleCode;
}): Promise<void> => {
  const { data } = await api.post<Envelope>(`${IAM_BASE}/admin/v1/users/role`, input);
  unwrap(data);
};

export type PasswordResetResult = {
  emailDelivered: boolean;
  /**
   * YALNIZ e-posta GIDEMEDIGINDE gelir ve YALNIZ BIR KEZ gorunur; sunucu bir
   * daha vermez (sifre zaten degisti, islem geri alinamaz). Bu yuzden ekranda
   * kapatilabilir bir bildirimde degil, kalici bir panelde ve elden teslim
   * uyarisiyla gosterilir.
   */
  tempPassword: string | null;
};

export const resetUserPassword = async (input: {
  userId: string;
}): Promise<PasswordResetResult> => {
  const { data } = await api.post<Envelope & { emailDelivered?: unknown; tempPassword?: unknown }>(
    `${IAM_BASE}/admin/v1/users/password-reset`,
    input,
  );
  unwrap(data);
  return {
    emailDelivered: data.emailDelivered === true,
    tempPassword: typeof data.tempPassword === 'string' ? data.tempPassword : null,
  };
};

/* -------------------------------------------------------------------------- */
/* Isletmeler                                                                  */
/* -------------------------------------------------------------------------- */

export type PlatformOrganizationRow = {
  id: string;
  organizationName: string;
  organizationTypeCode: string;
  organizationEmail: string;
  organizationPhone: string;
  organizationCity: string;
  organizationDistrict: string;
  businessChamber: string;
  businessNumber: string;
  isActive: boolean;
  memberCount: number;
  activeMemberCount: number;
  createdAt: string;
};

const toOrganizationRow = (raw: Record<string, unknown>): PlatformOrganizationRow => ({
  id: asId(raw.id),
  organizationName: asText(raw.organizationName),
  organizationTypeCode: asText(raw.organizationTypeCode),
  organizationEmail: asText(raw.organizationEmail),
  organizationPhone: asText(raw.organizationPhone),
  organizationCity: asText(raw.organizationCity),
  organizationDistrict: asText(raw.organizationDistrict),
  businessChamber: asText(raw.businessChamber),
  businessNumber: asText(raw.businessNumber),
  isActive: asBool(raw.isActive),
  memberCount: asCount(raw.memberCount),
  activeMemberCount: asCount(raw.activeMemberCount),
  createdAt: asText(raw.createdAt),
});

export type OrganizationListFilters = { q: string; isActive: boolean | null };

export const EMPTY_ORGANIZATION_FILTERS: OrganizationListFilters = { q: '', isActive: null };

export const fetchPlatformOrganizations = (input: {
  filters: OrganizationListFilters;
  offset: number;
  snapshotAt: string | null;
}): Promise<Paged<PlatformOrganizationRow>> => {
  const trimmed = input.filters.q.trim();
  return pagedGet(
    IAM_BASE,
    '/admin/v1/organizations',
    {
      ...pageParams({ offset: input.offset, snapshotAt: input.snapshotAt }),
      ...(trimmed === '' ? {} : { q: trimmed }),
      ...(input.filters.isActive === null ? {} : { isActive: String(input.filters.isActive) }),
    },
    toOrganizationRow,
  );
};

export type PlatformOrganizationMember = {
  /** ORGANIZASYON UYELIGI kimligi. */
  id: string;
  /** KULLANICI kimligi — kullanici detayina baglanti bununla kurulur. */
  appUserId: string;
  memberType: MemberType | null;
  isActive: boolean;
  userFullName: string;
  userEmail: string;
  userPhone: string;
  permissionCount: number;
  joinedAt: string | null;
};

const toOrganizationMember = (raw: Record<string, unknown>): PlatformOrganizationMember => ({
  id: asId(raw.id),
  appUserId: asId(raw.appUserId),
  memberType: asMemberType(raw.organizationMemberTypeCode),
  isActive: asBool(raw.isActive),
  userFullName: asText(raw.userFullName),
  userEmail: asText(raw.userEmail),
  userPhone: asText(raw.userPhone),
  permissionCount: asCount(raw.permissionCount),
  joinedAt: asNullableText(raw.joinedAt),
});

export type PlatformOrganizationDetail = {
  organization: PlatformOrganizationRow;
  members: PlatformOrganizationMember[];
};

/**
 * Detay ucu `{ items, page }` sozlesmesini KULLANMAZ: uyeler `memberList`
 * altinda gelir (canli servise karsi dogrulandi). Ikisi karistirilmamali.
 */
export const fetchPlatformOrganization = async (
  organizationId: string,
): Promise<PlatformOrganizationDetail> => {
  const { data } = await api.get<
    Envelope & { organization?: unknown; memberList?: unknown }
  >(`${IAM_BASE}/admin/v1/organizations/detail`, { params: { organizationId } });
  unwrap(data);

  if (typeof data.organization !== 'object' || data.organization === null) {
    throw new ApiShapeError('/admin/v1/organizations/detail.organization nesne degil');
  }
  if (!Array.isArray(data.memberList)) {
    throw new ApiShapeError('/admin/v1/organizations/detail.memberList dizi degil');
  }

  return {
    organization: toOrganizationRow(data.organization as Record<string, unknown>),
    members: data.memberList
      .filter((item): item is Record<string, unknown> => typeof item === 'object' && item !== null)
      .map(toOrganizationMember),
  };
};

/**
 * Isletme aktifligi. Sunucu ayni islemde ETKIN HER UYENIN jetonlarini iptal
 * eder ve kac uyenin etkilendigini doner — onay diyalogu bu sayiyi ONCEDEN
 * (uye sayisindan) soyler, sonuc bildirimi ise sunucunun DONDURDUGUNU.
 */
export const setOrganizationStatus = async (input: {
  organizationId: string;
  isActive: boolean;
}): Promise<{ revokedMemberCount: number }> => {
  const { data } = await api.post<Envelope & { revokedMemberCount?: unknown }>(
    `${IAM_BASE}/admin/v1/organizations/status`,
    input,
  );
  unwrap(data);
  return { revokedMemberCount: asCount(data.revokedMemberCount) };
};

/* -------------------------------------------------------------------------- */
/* Isletme ve uye YARATMA (2026-08-30)                                         */
/* -------------------------------------------------------------------------- */

/**
 * Yaratma sonucu — `PasswordResetResult` ile AYNI sozlesme ve ayni gerekce:
 * gecici sifre YALNIZ e-posta gidemediginde ve YALNIZ BIR KEZ gelir.
 */
export type ProvisionResult = {
  emailDelivered: boolean;
  tempPassword: string | null;
};

const toProvisionResult = (data: {
  emailDelivered?: unknown;
  tempPassword?: unknown;
}): ProvisionResult => ({
  emailDelivered: data.emailDelivered === true,
  tempPassword: typeof data.tempPassword === 'string' ? data.tempPassword : null,
});

/**
 * Isletme + KURUCU YONETICISI tek islemde.
 *
 * Self-servis kayit kapatildigindan (2026-08-30) organizasyon yaratmanin TEK yolu bu uc.
 * Sunucu dordunu tek transaction'da yazar: organizasyon, kullanici, rol satiri, admin uyeligi.
 *
 * `businessChamber` / `businessNumber` YALNIZ 'exchange' icin zorunlu — musteri
 * organizasyonunda oda/sicil kavrami yok ve kolonlar NULL kalir.
 */
export const createOrganization = async (input: {
  organizationName: string;
  organizationTypeCode: 'customer' | 'exchange';
  founderFullName: string;
  founderEmail: string;
  businessChamber?: string;
  businessNumber?: string;
  organizationCity?: string;
  organizationDistrict?: string;
}): Promise<ProvisionResult> => {
  const { data } = await api.post<Envelope & { emailDelivered?: unknown; tempPassword?: unknown }>(
    `${IAM_BASE}/admin/v1/organizations/create`,
    input,
  );
  unwrap(data);
  return toProvisionResult(data);
};

/**
 * HERHANGI bir isletmeye uye ekler.
 *
 * Isletme yoneticisinin kendi ekibine kullandigi ucun (`/v1/organizations/me/members/create`)
 * platform kapsamli ikizi: fark yalnizca organizasyonun govdeden gelmesi. Yeni kullanicinin
 * `user_type`'ini sunucu ORGANIZASYONUN TIPINDEN turetir.
 *
 * `initialPermissions` GONDERILMIYOR: grant edilebilir yetkiler buro ici operasyon
 * yetkileridir ve onlari yonetenin isletme yoneticisi olmasi dogru — platform yoneticisi
 * hesabi acar, gunluk yetkiyi isletme dagitir. (Musteri personeline gereken `viewAccounts`
 * varsayilanini sunucu kendisi veriyor.)
 */
export const provisionOrganizationMember = async (input: {
  organizationId: string;
  invitedFullName: string;
  invitedEmail: string;
  memberTypeCode: 'admin' | 'staff';
}): Promise<ProvisionResult> => {
  const { data } = await api.post<Envelope & { emailDelivered?: unknown; tempPassword?: unknown }>(
    `${IAM_BASE}/admin/v1/organizations/members/create`,
    input,
  );
  unwrap(data);
  return toProvisionResult(data);
};

/* -------------------------------------------------------------------------- */
/* Isletmenin banka hesaplari (IBAN platform yonetiminde)                      */
/* -------------------------------------------------------------------------- */

export type PlatformAccountRow = {
  id: string;
  accountName: string;
  /** Banka kodu ('ziraat', 'isbank'…) — `features/accounts/banks.ts` ile ayni kume. */
  accountCode: string;
  accountCurrency: string;
  /** TAM IBAN. Ekranda maskeli gosterilir; acikca "goster" denince ortaya cikar. */
  accountIbanCode: string;
  isActive: boolean;
};

const toAccountRow = (raw: Record<string, unknown>): PlatformAccountRow => ({
  id: asId(raw.id),
  accountName: typeof raw.accountName === 'string' ? raw.accountName : '',
  accountCode: typeof raw.accountCode === 'string' ? raw.accountCode : '',
  accountCurrency: typeof raw.accountCurrency === 'string' ? raw.accountCurrency : '',
  accountIbanCode: typeof raw.accountIbanCode === 'string' ? raw.accountIbanCode : '',
  // Yalniz acik `false` pasif sayilir; alan hic gelmezse aktif varsayilir.
  isActive: raw.isActive !== false,
});

export const fetchOrganizationAccounts = async (
  organizationId: string,
): Promise<PlatformAccountRow[]> => {
  const { data } = await api.get<Envelope & { accountList?: unknown }>(
    `${IAM_BASE}/admin/v1/accounts`,
    { params: { organizationId } },
  );
  unwrap(data);
  // Bos diziye DUSMUYOR: sozlesme ayrismasini "hesap yok" diye gostermek, gercek bir hatayi
  // normal bir bos ekrana cevirirdi.
  if (!Array.isArray(data.accountList)) {
    throw new ApiShapeError('/admin/v1/accounts.accountList dizi degil');
  }
  return data.accountList
    .filter((item): item is Record<string, unknown> => typeof item === 'object' && item !== null)
    .map(toAccountRow);
};

/**
 * Hesap olustur/guncelle. Ayni uc ikisini de yapar; ayirici govdedeki `id`
 * (repo genelindeki upsert idiomu).
 *
 * SILME UCU YOK: `accountIsActive: false` pasiflestirmedir. Bir IBAN'i gercekten silmek,
 * o hesaba yapilmis gecmis islemlerin izini koparirdi.
 */
export const saveOrganizationAccount = async (input: {
  id?: string;
  organizationId: string;
  accountName: string;
  accountCode: string;
  accountCurrency: string;
  accountIbanCode: string;
  accountType?: string;
  accountLabel?: string;
  accountIsActive?: boolean;
}): Promise<void> => {
  const { data } = await api.post<Envelope>(`${IAM_BASE}/admin/v1/accounts`, input);
  unwrap(data);
};

/* -------------------------------------------------------------------------- */
/* Denetim kaydi (platform genelinde)                                          */
/* -------------------------------------------------------------------------- */

export type PlatformAuditEntry = {
  id: string;
  /** NULL = PLATFORM islemi (isletme icinde degil, isletmelerin ustunde). */
  organizationId: string | null;
  organizationName: string | null;
  actionCode: string;
  actorUserId: string | null;
  actorName: string;
  targetType: string;
  targetId: string | null;
  createdAt: string;
  /** actionData.permissionCodes uzunlugu; yoksa null. */
  permissionCount: number | null;
};

const toAuditEntry = (raw: Record<string, unknown>): PlatformAuditEntry => {
  const actionData = (
    typeof raw.actionData === 'object' && raw.actionData !== null ? raw.actionData : {}
  ) as Record<string, unknown>;
  const codes = actionData.permissionCodes;
  return {
    id: asId(raw.id),
    organizationId: asNullableId(raw.organizationId),
    organizationName: asNullableText(raw.organizationName),
    actionCode: asText(raw.actionCode),
    actorUserId: asNullableId(raw.actorUserId),
    actorName: asText(raw.actorName),
    targetType: asText(raw.targetType),
    targetId: asNullableId(raw.targetId),
    createdAt: asText(raw.createdAt),
    permissionCount: Array.isArray(codes) ? codes.length : null,
  };
};

export type AuditFilters = {
  actionCode: string | null;
  organizationId: string | null;
  actorUserId: string | null;
};

export const EMPTY_AUDIT_FILTERS: AuditFilters = {
  actionCode: null,
  organizationId: null,
  actorUserId: null,
};

export const fetchPlatformAuditLog = (input: {
  filters: AuditFilters;
  offset: number;
  snapshotAt: string | null;
}): Promise<Paged<PlatformAuditEntry>> =>
  pagedGet(
    IAM_BASE,
    '/admin/v1/audit-log',
    {
      ...pageParams({ offset: input.offset, snapshotAt: input.snapshotAt }),
      ...(input.filters.actionCode === null ? {} : { actionCode: input.filters.actionCode }),
      ...(input.filters.organizationId === null
        ? {}
        : { organizationId: input.filters.organizationId }),
      ...(input.filters.actorUserId === null ? {} : { actorUserId: input.filters.actorUserId }),
    },
    toAuditEntry,
  );

/* -------------------------------------------------------------------------- */
/* Metrikler — IAM (kullanici)                                                 */
/* -------------------------------------------------------------------------- */

export const METRIC_PERIODS = ['day', 'week', 'month'] as const;
export type MetricPeriod = (typeof METRIC_PERIODS)[number];

export type UserMetricsTotals = {
  customers: number;
  exchanges: number;
  active: number;
  inactive: number;
  organizations: number;
  activeOrganizations: number;
};

export type UserMetricsPoint = {
  bucket: string;
  signups: number;
  customerSignups: number;
  exchangeSignups: number;
};

export type UserMetrics = {
  /**
   * ⚠ CANLI GORUNTU — `from`/`to` PENCERESINI YOK SAYAR.
   *
   * Sunucu bu bloku "su anda sistemde kac kullanici var" olarak hesapliyor;
   * secilen tarih araligi YALNIZ `series`i etkiler. Ekranda bu yuzden ayri
   * baslik ve acik bir not ile cizilir — aksi halde "son 30 gunde 5 kullanici"
   * gibi okunur ve tamamen yanlis olur.
   */
  totals: UserMetricsTotals;
  series: UserMetricsPoint[];
  from: string | null;
  to: string | null;
};

export const fetchUserMetrics = async (input: {
  period: MetricPeriod;
  from: string | null;
  to: string | null;
}): Promise<UserMetrics> => {
  const { data } = await api.get<
    Envelope & { totals?: unknown; series?: unknown; from?: unknown; to?: unknown }
  >(`${IAM_BASE}/admin/v1/metrics/users`, {
    params: {
      period: input.period,
      ...(input.from === null ? {} : { from: input.from }),
      ...(input.to === null ? {} : { to: input.to }),
    },
  });
  unwrap(data);

  if (typeof data.totals !== 'object' || data.totals === null) {
    throw new ApiShapeError('/admin/v1/metrics/users.totals nesne degil');
  }
  if (!Array.isArray(data.series)) {
    throw new ApiShapeError('/admin/v1/metrics/users.series dizi degil');
  }

  const totals = data.totals as Record<string, unknown>;
  const byUserType = (
    typeof totals.byUserType === 'object' && totals.byUserType !== null ? totals.byUserType : {}
  ) as Record<string, unknown>;

  return {
    totals: {
      customers: asCount(byUserType.customer),
      exchanges: asCount(byUserType.exchange),
      active: asCount(totals.active),
      inactive: asCount(totals.inactive),
      organizations: asCount(totals.organizations),
      activeOrganizations: asCount(totals.activeOrganizations),
    },
    series: data.series
      .filter((item): item is Record<string, unknown> => typeof item === 'object' && item !== null)
      .map((raw) => ({
        bucket: asText(raw.bucket),
        signups: asCount(raw.signups),
        customerSignups: asCount(raw.customerSignups),
        exchangeSignups: asCount(raw.exchangeSignups),
      })),
    from: asNullableText(data.from),
    to: asNullableText(data.to),
  };
};

/* -------------------------------------------------------------------------- */
/* Metrikler — Currency (islem / teklif / hacim)                               */
/* -------------------------------------------------------------------------- */

export type OverviewMetrics = {
  tasks: { total: number; won: number; winRate: number };
  offers: {
    offerCount: number;
    offeredTaskCount: number;
    offeringUserCount: number;
    offeringOrganizationCount: number;
  };
  /**
   * Islem tipi basina hacim. BIRIMLER FARKLI (2026-09-23, karar Q13): `totalAmount` /
   * `wonAmount` islem para biriminde (`currency`, ör. EUR), `wonCounterValue` karsi para
   * biriminde (`counterCurrency`; EUR/USD'de USD, digerlerinde TRY). Satirlar arasi toplam
   * ALINMAZ. Birim gelmezse (eski sunucu) `null`.
   */
  volume: {
    taskTypeCode: string;
    currency: string | null;
    counterCurrency: string | null;
    taskCount: number;
    wonTaskCount: number;
    totalAmount: number;
    wonAmount: number;
    wonCounterValue: number;
  }[];
};

export const fetchOverviewMetrics = async (): Promise<OverviewMetrics> => {
  const { data } = await api.get<
    Envelope & { tasks?: unknown; offers?: unknown; volume?: unknown }
  >(`${CURRENCY_BASE}/admin/v1/metrics/overview`);
  unwrap(data);

  if (typeof data.tasks !== 'object' || data.tasks === null) {
    throw new ApiShapeError('/admin/v1/metrics/overview.tasks nesne degil');
  }
  if (!Array.isArray(data.volume)) {
    throw new ApiShapeError('/admin/v1/metrics/overview.volume dizi degil');
  }

  const tasks = data.tasks as Record<string, unknown>;
  const offers = (
    typeof data.offers === 'object' && data.offers !== null ? data.offers : {}
  ) as Record<string, unknown>;

  return {
    tasks: {
      total: asCount(tasks.total),
      won: asCount(tasks.won),
      winRate: asNumber(tasks.winRate),
    },
    offers: {
      offerCount: asCount(offers.offerCount),
      offeredTaskCount: asCount(offers.offeredTaskCount),
      offeringUserCount: asCount(offers.offeringUserCount),
      offeringOrganizationCount: asCount(offers.offeringOrganizationCount),
    },
    volume: data.volume
      .filter((item): item is Record<string, unknown> => typeof item === 'object' && item !== null)
      .map((raw) => ({
        taskTypeCode: asText(raw.taskTypeCode),
        currency: asNullableText(raw.currency),
        counterCurrency: asNullableText(raw.counterCurrency),
        taskCount: asCount(raw.taskCount),
        wonTaskCount: asCount(raw.wonTaskCount),
        totalAmount: asNumber(raw.totalAmount),
        wonAmount: asNumber(raw.wonAmount),
        wonCounterValue: asNumber(raw.wonCounterValue),
      })),
  };
};

export const TIMESERIES_METRICS = ['tasks', 'offers', 'volume'] as const;
export type TimeseriesMetric = (typeof TIMESERIES_METRICS)[number];

/**
 * Zaman serisi noktasi. `currency` YALNIZ hacim serisinde gelir (2026-09-23, karar Q13) ve
 * o zaman AYNI kovada birden cok nokta olur — birim basina bir tane. Bu noktalar toplanirsa
 * USD ile TRY ayni cubukta birlesir; ekran birimi secip suzer (PlatformMetricsPage).
 */
export type TimeseriesPoint = { bucket: string; value: number; currency: string | null };

/** Hacim serisindeki birimler, sunucu sirasiyla (ilk gorulen once). */
export const seriesCurrencies = (points: readonly TimeseriesPoint[]): string[] => [
  ...new Set(points.map((point) => point.currency).filter((c): c is string => c !== null)),
];

export const fetchTimeseries = async (input: {
  metric: TimeseriesMetric;
  period: MetricPeriod;
}): Promise<TimeseriesPoint[]> => {
  const { data } = await api.get<Envelope & { series?: unknown }>(
    `${CURRENCY_BASE}/admin/v1/metrics/timeseries`,
    { params: { metric: input.metric, period: input.period } },
  );
  unwrap(data);
  if (!Array.isArray(data.series)) {
    throw new ApiShapeError('/admin/v1/metrics/timeseries.series dizi degil');
  }
  return data.series
    .filter((item): item is Record<string, unknown> => typeof item === 'object' && item !== null)
    .map((raw) => ({
      bucket: asText(raw.bucket),
      value: asNumber(raw.value),
      currency: asNullableText(raw.currency),
    }));
};

export type ActivityEvent = {
  /** 'task' | 'offer' — sunucu baska bir tur eklerse ham hali gosterilir. */
  eventType: string;
  entityId: string;
  occurredAt: string;
  taskId: string | null;
  taskTypeCode: string;
  taskStatusCode: string;
  actorFullName: string;
  actorUserType: UserType | null;
  actorOrganizationName: string | null;
};

export const fetchActivity = (input: {
  offset: number;
  snapshotAt: string | null;
}): Promise<Paged<ActivityEvent>> =>
  pagedGet(
    CURRENCY_BASE,
    '/admin/v1/activity',
    pageParams(input),
    (raw): ActivityEvent => ({
      eventType: asText(raw.eventType),
      entityId: asId(raw.entityId),
      occurredAt: asText(raw.occurredAt),
      taskId: asNullableId(raw.taskId),
      taskTypeCode: asText(raw.taskTypeCode),
      taskStatusCode: asText(raw.taskStatusCode),
      actorFullName: asText(raw.actorFullName),
      actorUserType: asUserType(raw.actorUserType),
      actorOrganizationName: asNullableText(raw.actorOrganizationName),
    }),
  );

/* -------------------------------------------------------------------------- */
/* Uyari alan islemler — cozum kuyrugu (2026-09-23, karar Q3)                  */
/* -------------------------------------------------------------------------- */

/**
 * PLATFORM GENELI uyarili islemler. 'warning' eskiden SON durumdu: supurucu musteriye
 * "incelemeye alindi" diyordu ama inceleyecek bir liste yoktu. Platform yoneticisi iki tarafla
 * gorusup islemi 'resolved' (doviz ulasti / anlasmazlik giderildi) ya da 'refunded' (musterinin
 * odemesi iade ettirildi) ile kapatir.
 *
 * IKI TARAFIN TELEFONU gelir: yonetici ikisini de arayacak. Ekran bunlari VARSAYILAN MASKELI
 * gosterir (kullanici listesiyle ayni kural).
 *
 * BIRIMLER: `taskAmount` islem para biriminde (task_type.currency; EUR/USD'de EUR),
 * `offerPrice` 1 birimin `counterCurrency` cinsinden fiyati. Tutar = ikisinin carpimi,
 * `counterCurrency` biriminde.
 */
export type WarnedTaskQueueRow = {
  taskId: string;
  taskTypeCode: string;
  taskAmount: number;
  taskIsBuying: boolean;
  /** Kazanan teklif silinmis/eksikse NULL. */
  offerPrice: number | null;
  counterCurrency: string;
  decidedAt: string | null;
  warnedAt: string | null;
  customerUserId: string;
  customerFullName: string;
  customerPhone: string;
  exchangeUserId: string | null;
  exchangeFullName: string;
  exchangePhone: string;
  exchangeOrganizationName: string | null;
};

/** Sunucunun kabul ettigi siralamalar (admin-scripts.js adminWarnedTaskSortMap). */
export const WARNED_QUEUE_SORTS = ['warnedAt:asc', 'warnedAt:desc'] as const;
export type WarnedQueueSort = (typeof WARNED_QUEUE_SORTS)[number];
/** Kuyruk: EN ESKI uyari once (sunucu varsayilaniyla ayni). */
export const WARNED_QUEUE_SORT_DEFAULT: WarnedQueueSort = 'warnedAt:asc';

const toWarnedTaskRow = (raw: Record<string, unknown>): WarnedTaskQueueRow => ({
  taskId: asId(raw.taskId),
  taskTypeCode: asText(raw.taskTypeCode),
  taskAmount: asNumber(raw.taskAmount),
  taskIsBuying: asBool(raw.taskIsBuying),
  offerPrice: raw.offerPrice === null || raw.offerPrice === undefined ? null : asNumber(raw.offerPrice),
  counterCurrency: asText(raw.counterCurrency),
  decidedAt: asNullableText(raw.decidedAt),
  warnedAt: asNullableText(raw.warnedAt),
  customerUserId: asId(raw.customerUserId),
  customerFullName: asText(raw.customerFullName),
  customerPhone: asText(raw.customerPhone),
  exchangeUserId: asNullableId(raw.exchangeUserId),
  exchangeFullName: asText(raw.exchangeFullName),
  exchangePhone: asText(raw.exchangePhone),
  exchangeOrganizationName: asNullableText(raw.exchangeOrganizationName),
});

export const fetchWarnedTaskQueue = (input: {
  offset: number;
  snapshotAt: string | null;
  sort: WarnedQueueSort;
}): Promise<Paged<WarnedTaskQueueRow>> =>
  pagedGet(
    CURRENCY_BASE,
    '/admin/v1/tasks/warned',
    pageParams({ offset: input.offset, snapshotAt: input.snapshotAt, sort: input.sort }),
    toWarnedTaskRow,
  );

export const TASK_RESOLUTIONS = ['resolved', 'refunded'] as const;
export type TaskResolution = (typeof TASK_RESOLUTIONS)[number];

/** Sunucu siniri (admin-service.js RESOLUTION_NOTE_MAX). */
export const RESOLUTION_NOTE_MAX = 1000;

/**
 * Uyarili islemi kapatir. Yalniz 'warning'den gecer; baska bir yonetici once davrandiysa
 * sunucu 409 TASK_NOT_IN_WARNING doner (metni labels.ts'te). GERI ALINAMAZ ve iki tarafa
 * bildirim gider — cagri yeri onay diyalogunun arkasinda.
 */
export const resolveWarnedTask = async (input: {
  taskId: string;
  resolution: TaskResolution;
  note: string;
}): Promise<void> => {
  const note = input.note.trim();
  const { data } = await api.post<Envelope>(`${CURRENCY_BASE}/admin/v1/tasks/resolve`, {
    taskId: input.taskId,
    resolution: input.resolution,
    ...(note === '' ? {} : { note }),
  });
  unwrap(data);
};

/* -------------------------------------------------------------------------- */
/* Duyurular                                                                   */
/* -------------------------------------------------------------------------- */

export const BROADCAST_AUDIENCES = ['customer', 'exchange', 'all'] as const;
export type BroadcastAudience = (typeof BROADCAST_AUDIENCES)[number];

/** Sunucu sinirlari (currency admin-service TITLE_MAX / BODY_MAX). */
export const BROADCAST_TITLE_MAX = 100;
export const BROADCAST_BODY_MAX = 500;

export type BroadcastDelivery = {
  /**
   * 'topic' | 'token'.
   *
   * ⚠ TOPIC KIPINDE `successCount: 1`, "FCM konuyu KABUL ETTI" demektir —
   * "1 cihaza ulasti" DEGIL. Kac cihazin aldigi bu kipte OLCULEMEZ. Ekran bu
   * sayiyi teslimat adedi gibi gostermez.
   */
  mode: string;
  topics: string[];
  /** Yalniz token kipinde anlamli. */
  tokenCount: number | null;
  successCount: number;
  failureCount: number;
};

export type BroadcastResult = { broadcastId: string; delivery: BroadcastDelivery };

const toDelivery = (raw: unknown): BroadcastDelivery => {
  const value = (typeof raw === 'object' && raw !== null ? raw : {}) as Record<string, unknown>;
  return {
    mode: asText(value.mode),
    topics: Array.isArray(value.topics) ? value.topics.map(asText).filter(Boolean) : [],
    tokenCount: value.tokenCount === undefined ? null : asCount(value.tokenCount),
    successCount: asCount(value.successCount),
    failureCount: asCount(value.failureCount),
  };
};

/**
 * TUM HEDEF KITLEYE gider ve GERI ALINAMAZ. Bu yuzden cagri yeri onay
 * diyalogunun arkasindadir (bkz. BroadcastComposer).
 *
 * Ag gecidi bu ucu IP basina 15 dakikada 10 istekle sinirliyor; 429 mesaji
 * error-message.ts uzerinden ayri bir metne cevriliyor.
 */
export const sendBroadcast = async (input: {
  audience: BroadcastAudience;
  title: string;
  body: string;
  targetUserId: string | null;
}): Promise<BroadcastResult> => {
  const { data } = await api.post<Envelope & { broadcastId?: unknown; delivery?: unknown }>(
    `${CURRENCY_BASE}/admin/v1/notifications/broadcast`,
    {
      audience: input.audience,
      title: input.title,
      body: input.body,
      ...(input.targetUserId === null ? {} : { targetUserId: input.targetUserId }),
    },
  );
  unwrap(data);
  return { broadcastId: asId(data.broadcastId), delivery: toDelivery(data.delivery) };
};

export type BroadcastHistoryEntry = {
  id: string;
  audience: string;
  targetUserId: string | null;
  title: string;
  body: string;
  createdAt: string;
  actorFullName: string;
  delivery: BroadcastDelivery;
};

export const fetchBroadcasts = (input: {
  offset: number;
  snapshotAt: string | null;
}): Promise<Paged<BroadcastHistoryEntry>> =>
  pagedGet(
    CURRENCY_BASE,
    '/admin/v1/notifications/broadcasts',
    pageParams(input),
    (raw): BroadcastHistoryEntry => ({
      id: asId(raw.id),
      audience: asText(raw.audience),
      targetUserId: asNullableId(raw.targetUserId),
      title: asText(raw.title),
      body: asText(raw.body),
      createdAt: asText(raw.createdAt),
      actorFullName: asText(raw.actorFullName),
      delivery: toDelivery(raw.deliveryResult),
    }),
  );
